import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  FileText,
  Trash2,
  Clock,
  CheckCircle,
  Send,
  Package,
  AlertCircle,
  ArrowLeft,
  Search
} from 'lucide-react';
import { useInquiryLists } from '../../hooks/useInquiryLists';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import UserLayout from './UserLayout';
import { SkeletonRows } from '../shared/Skeleton';
import PageHeader from '../shared/PageHeader';
import Card from '../shared/Card';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import StatusBadge, { inquiryStatusTone } from '../shared/StatusBadge';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent';

const InquiryLists = () => {
  const history = useHistory();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  
  // Bulk inquiry state
  const [showBulkInquiryModal, setShowBulkInquiryModal] = useState(false);
  const [bulkInquiryItems, setBulkInquiryItems] = useState([]);
  const [bulkInquiryStep, setBulkInquiryStep] = useState('choose'); // 'choose', 'create', 'select'
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);

  // Get user ID
  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });
  }, []);

  const { inquiryLists, loading, createList, deleteList } = useInquiryLists(user?.id);

  // Detect bulk inquiry action from URL parameter
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    
    if (action === 'create-from-bulk') {
      // Retrieve items from sessionStorage
      const storedItems = sessionStorage.getItem('bulkInquiryItems');
      
      if (storedItems) {
        try {
          const items = JSON.parse(storedItems);
          if (items && items.length > 0) {
            setBulkInquiryItems(items);
            setShowBulkInquiryModal(true);
            setBulkInquiryStep('choose');
          }
        } catch (error) {
          console.error('Error parsing bulk inquiry items:', error);
          showError('Failed to load bulk inquiry items');
        }
      }
      
      // Clean up URL parameter
      history.replace('/user/inquiry-lists');
    }
  }, [location.search, history]);

  // Status badge: icon + label here, colour from the portal-wide inquiry tone map
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { icon: FileText, label: 'Draft' },
      submitted: { icon: Send, label: 'Submitted' },
      under_review: { icon: Clock, label: 'Under Review' },
      quoted: { icon: CheckCircle, label: 'Quoted' },
      converted: { icon: Package, label: 'Converted' },
      cancelled: { icon: AlertCircle, label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <StatusBadge tone={inquiryStatusTone(status)} icon={<Icon className="w-3 h-3" aria-hidden="true" />}>
        {config.label}
      </StatusBadge>
    );
  };

  // Filter inquiry lists
  const filteredLists = inquiryLists.filter(list => {
    const matchesStatus = filterStatus === 'all' || list.status === filterStatus;
    const matchesSearch = list.list_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (list.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Handle create inquiry list
  const handleCreateList = async () => {
    if (!newListName.trim()) {
      showError('Please enter a list name');
      return;
    }

    try {
      await createList(newListName, newListDescription || null);
      showNotification('Inquiry list created successfully');
      setShowCreateModal(false);
      setNewListName('');
      setNewListDescription('');
    } catch (error) {
      showError('Failed to create inquiry list: ' + error.message);
    }
  };

  // Handle delete inquiry list
  const handleDeleteList = async (listId, listName) => {
    if (!window.confirm(`Are you sure you want to delete "${listName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(listId);
      await deleteList(listId);
      showNotification('Inquiry list deleted successfully');
    } catch (error) {
      showError('Failed to delete inquiry list: ' + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Helper function to add a single item to a list
  const addItemToList = async (listId, itemData) => {
    const { error } = await supabase
      .rpc('add_inquiry_item', {
        inquiry_list_id: listId,
        part_number: itemData.partNumber,
        description: itemData.description,
        quantity: itemData.quantity,
        notes: itemData.notes
      });
      
    if (error) throw error;
  };

  // Handle creating a new list with bulk items
  const handleCreateListWithBulkItems = async () => {
    if (!newListName.trim()) {
      showError('Please enter a list name');
      return;
    }

    try {
      setIsProcessingBulk(true);
      
      // Create the list
      console.log('Creating inquiry list with name:', newListName);
      const listData = await createList(newListName, newListDescription || null);
      console.log('List created, data returned:', listData);
      
      // Handle both UUID string and object with id property
      const newListId = listData?.id || listData;
      console.log('Using list ID:', newListId);
      
      if (!newListId) {
        throw new Error('Failed to get list ID from created list');
      }
      
      // Add all bulk items to the new list
      console.log(`Adding ${bulkInquiryItems.length} items to list ${newListId}`);
      let successCount = 0;
      let failedItems = [];
      
      for (let i = 0; i < bulkInquiryItems.length; i++) {
        const item = bulkInquiryItems[i];
        try {
          console.log(`Adding item ${i + 1}/${bulkInquiryItems.length}:`, item.PartNumber);
          await addItemToList(newListId, {
            partNumber: item.PartNumber,
            description: item.Description,
            quantity: item.Quantity,
            notes: `From bulk query - Stock Status: ${item.stockStatus}`
          });
          successCount++;
          console.log(`Successfully added item ${i + 1}`);
        } catch (itemError) {
          console.error(`Failed to add item ${item.PartNumber}:`, itemError);
          failedItems.push({ item, error: itemError.message });
        }
      }
      
      console.log(`Added ${successCount} of ${bulkInquiryItems.length} items`);
      
      // Clear sessionStorage
      sessionStorage.removeItem('bulkInquiryItems');
      
      // Show appropriate message
      if (failedItems.length === 0) {
        showNotification(`Created inquiry list with ${successCount} items`);
      } else if (successCount > 0) {
        showNotification(`Created inquiry list with ${successCount} items. ${failedItems.length} items failed to add.`, 'warning');
      } else {
        throw new Error('Failed to add any items to the list');
      }
      
      // Reset modal state
      setShowBulkInquiryModal(false);
      setBulkInquiryItems([]);
      setNewListName('');
      setNewListDescription('');
      setBulkInquiryStep('choose');
      
      // Redirect to the new list
      history.push(`/user/inquiry-list/${newListId}`);
    } catch (error) {
      console.error('Error in handleCreateListWithBulkItems:', error);
      showError('Failed to create inquiry list: ' + error.message);
    } finally {
      setIsProcessingBulk(false);
    }
  };

  // Handle adding bulk items to an existing list
  const handleAddBulkItemsToExistingList = async (listId) => {
    try {
      setIsProcessingBulk(true);
      
      console.log(`Adding ${bulkInquiryItems.length} items to existing list ${listId}`);
      let successCount = 0;
      let failedItems = [];
      
      // Add all bulk items to the selected list
      for (let i = 0; i < bulkInquiryItems.length; i++) {
        const item = bulkInquiryItems[i];
        try {
          console.log(`Adding item ${i + 1}/${bulkInquiryItems.length}:`, item.PartNumber);
          await addItemToList(listId, {
            partNumber: item.PartNumber,
            description: item.Description,
            quantity: item.Quantity,
            notes: `From bulk query - Stock Status: ${item.stockStatus}`
          });
          successCount++;
          console.log(`Successfully added item ${i + 1}`);
        } catch (itemError) {
          console.error(`Failed to add item ${item.PartNumber}:`, itemError);
          failedItems.push({ item, error: itemError.message });
        }
      }
      
      console.log(`Added ${successCount} of ${bulkInquiryItems.length} items`);
      
      // Clear sessionStorage
      sessionStorage.removeItem('bulkInquiryItems');
      
      // Show appropriate message
      if (failedItems.length === 0) {
        showNotification(`Added ${successCount} items to inquiry list`);
      } else if (successCount > 0) {
        showNotification(`Added ${successCount} items. ${failedItems.length} items failed to add.`, 'warning');
      } else {
        throw new Error('Failed to add any items to the list');
      }
      
      // Reset modal state
      setShowBulkInquiryModal(false);
      setBulkInquiryItems([]);
      setBulkInquiryStep('choose');
      
      // Redirect to the list
      history.push(`/user/inquiry-list/${listId}`);
    } catch (error) {
      console.error('Error in handleAddBulkItemsToExistingList:', error);
      showError('Failed to add items to inquiry list: ' + error.message);
    } finally {
      setIsProcessingBulk(false);
    }
  };

  // Handle closing bulk inquiry modal
  const handleCloseBulkInquiryModal = () => {
    setShowBulkInquiryModal(false);
    setBulkInquiryItems([]);
    setBulkInquiryStep('choose');
    sessionStorage.removeItem('bulkInquiryItems');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const header = (
    <PageHeader
      title="Inquiry Lists (RFQ)"
      description="Create and manage your request for quote (RFQ) inquiry lists"
      actions={
        <Button onClick={() => setShowCreateModal(true)} icon={<Plus className="w-4 h-4" aria-hidden="true" />}>
          New Inquiry List
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <UserLayout>
        <div className="space-y-6">
          {header}
          <SkeletonRows rows={4} />
        </div>
      </UserLayout>
    );
  }

  const openList = (listId) => history.push(`/user/inquiry-list/${listId}`);
  const choiceTile = 'w-full p-4 border border-gray-300 rounded-lg hover:border-brand hover:bg-gray-50 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50';

  return (
    <UserLayout>
      <div className="space-y-6">
      {header}

      {/* Search and status filter */}
      <Card padding="sm">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="relative flex-1 max-w-md">
            <label htmlFor="inquiry-search" className="sr-only">Search inquiry lists</label>
            <input
              id="inquiry-search"
              type="text"
              placeholder="Search inquiry lists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
            <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>

          <div className="max-w-full overflow-x-auto">
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-sm" role="group" aria-label="Filter by status">
              {['all', 'draft', 'submitted', 'quoted', 'converted'].map((status, i) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  aria-pressed={filterStatus === status}
                  className={`px-3 py-2 font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${i > 0 ? 'border-l border-gray-200' : ''} ${
                    filterStatus === status
                      ? 'bg-brand-dark text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Inquiry Lists Grid */}
      {filteredLists.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<FileText className="h-12 w-12" />}
            title={searchTerm || filterStatus !== 'all' ? 'No inquiry lists found' : 'No inquiry lists yet'}
            description={searchTerm || filterStatus !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Create your first inquiry list to get started'}
            action={!searchTerm && filterStatus === 'all' && (
              <Button onClick={() => setShowCreateModal(true)} icon={<Plus className="w-4 h-4" aria-hidden="true" />}>
                Create Inquiry List
              </Button>
            )}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredLists.map(list => (
              <motion.div
                key={list.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card
                  hover
                  role="link"
                  tabIndex={0}
                  aria-label={`Open inquiry list ${list.list_name}`}
                  onClick={() => openList(list.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target === e.currentTarget) openList(list.id);
                  }}
                  className="h-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {list.list_name}
                      </h3>
                      {list.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {list.description}
                        </p>
                      )}
                    </div>
                    {list.status === 'draft' && (
                      <Button
                        variant="ghostDanger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteList(list.id, list.list_name);
                        }}
                        loading={deletingId === list.id}
                        ariaLabel={`Delete inquiry list ${list.list_name}`}
                        icon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                      />
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Status:</span>
                      {getStatusBadge(list.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Items:</span>
                      <span className="font-medium text-gray-900 tabular-nums">{list.item_count}</span>
                    </div>
                    {list.quote_reference && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Quote Ref:</span>
                        <span className="font-medium text-gray-900">{list.quote_reference}</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-200 text-xs text-gray-500 space-y-1">
                    <div>Created: {formatDate(list.created_at)}</div>
                    {list.submitted_at && (
                      <div>Submitted: {formatDate(list.submitted_at)}</div>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Bulk Inquiry Modal */}
      <AnimatePresence>
        {showBulkInquiryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={handleCloseBulkInquiryModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-inquiry-title"
              className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <h2 id="bulk-inquiry-title" className="text-lg font-semibold text-gray-900 mb-4">Add Bulk Query Items to Inquiry List</h2>

              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  You have <strong>{bulkInquiryItems.length} items</strong> from your bulk query ready to add to an inquiry list.
                </p>
              </div>

              {bulkInquiryStep === 'choose' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Choose how you want to proceed:</p>

                  <button
                    onClick={() => {
                      setBulkInquiryStep('create');
                      setNewListName(`Bulk Query - ${new Date().toLocaleDateString()}`);
                    }}
                    className={choiceTile}
                  >
                    <div className="flex items-center gap-3">
                      <Plus className="text-gray-500 flex-shrink-0" size={24} aria-hidden="true" />
                      <div>
                        <div className="font-semibold text-gray-900">Create New Inquiry List</div>
                        <div className="text-sm text-gray-600">Start a fresh inquiry list with these items</div>
                      </div>
                    </div>
                  </button>

                  {inquiryLists.filter(list => list.status === 'draft').length > 0 && (
                    <button
                      onClick={() => setBulkInquiryStep('select')}
                      className={choiceTile}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="text-gray-500 flex-shrink-0" size={24} aria-hidden="true" />
                        <div>
                          <div className="font-semibold text-gray-900">Add to Existing List</div>
                          <div className="text-sm text-gray-600">Add items to one of your draft inquiry lists</div>
                        </div>
                      </div>
                    </button>
                  )}

                  <Button variant="secondary" onClick={handleCloseBulkInquiryModal} className="w-full">
                    Cancel
                  </Button>
                </div>
              )}

              {bulkInquiryStep === 'create' && (
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setBulkInquiryStep('choose');
                      setNewListName('');
                      setNewListDescription('');
                    }}
                    icon={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
                  >
                    Back
                  </Button>

                  <div>
                    <label htmlFor="bulk-list-name" className="block text-sm font-medium text-gray-700 mb-1">
                      List Name *
                    </label>
                    <input
                      id="bulk-list-name"
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="e.g., Hydraulic Parts Q4 2025"
                      className={inputClass}
                      autoFocus
                    />
                  </div>

                  <div>
                    <label htmlFor="bulk-list-description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      id="bulk-list-description"
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      placeholder="Add details about this inquiry list..."
                      rows={3}
                      className={`${inputClass} resize-none`}
                    />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2">Items to be added:</p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {bulkInquiryItems.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="text-sm text-gray-600">
                          • {item.PartNumber} - {item.Description} (Qty: {item.Quantity})
                        </div>
                      ))}
                      {bulkInquiryItems.length > 5 && (
                        <div className="text-sm text-gray-500 italic">
                          ... and {bulkInquiryItems.length - 5} more items
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setBulkInquiryStep('choose');
                        setNewListName('');
                        setNewListDescription('');
                      }}
                      disabled={isProcessingBulk}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateListWithBulkItems}
                      disabled={!newListName.trim()}
                      loading={isProcessingBulk}
                      className="flex-1"
                    >
                      {isProcessingBulk ? 'Creating...' : 'Create List & Add Items'}
                    </Button>
                  </div>
                </div>
              )}

              {bulkInquiryStep === 'select' && (
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setBulkInquiryStep('choose')}
                    icon={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
                  >
                    Back
                  </Button>

                  <p className="text-sm text-gray-600">Select an existing draft inquiry list:</p>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {inquiryLists
                      .filter(list => list.status === 'draft')
                      .map(list => (
                        <button
                          key={list.id}
                          onClick={() => handleAddBulkItemsToExistingList(list.id)}
                          disabled={isProcessingBulk}
                          className={choiceTile}
                        >
                          <div className="font-semibold text-gray-900">{list.list_name}</div>
                          {list.description && (
                            <div className="text-sm text-gray-600 mt-1">{list.description}</div>
                          )}
                          <div className="text-sm text-gray-500 mt-2">
                            Current items: {list.item_count} • Created: {formatDate(list.created_at)}
                          </div>
                        </button>
                      ))}
                  </div>

                  <Button
                    variant="secondary"
                    onClick={() => setBulkInquiryStep('choose')}
                    disabled={isProcessingBulk}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-inquiry-title"
              className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full"
            >
              <h2 id="create-inquiry-title" className="text-lg font-semibold text-gray-900 mb-4">Create New Inquiry List</h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="new-list-name" className="block text-sm font-medium text-gray-700 mb-1">
                    List Name *
                  </label>
                  <input
                    id="new-list-name"
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="e.g., Hydraulic Parts Q4 2025"
                    className={inputClass}
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="new-list-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    id="new-list-description"
                    value={newListDescription}
                    onChange={(e) => setNewListDescription(e.target.value)}
                    placeholder="Add details about this inquiry list..."
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewListName('');
                    setNewListDescription('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateList}
                  disabled={!newListName.trim()}
                  className="flex-1"
                >
                  Create List
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </UserLayout>
  );
};

export default InquiryLists;
